import { Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "@/constants/brand";
import { cn } from "@/lib/utils";

interface LogoProps {
  /** Variante do logo */
  variant?: "full" | "icon" | "text";
  /** Tamanho do logo */
  size?: "sm" | "md" | "lg";
  /** Se deve ser um link para a home */
  asLink?: boolean;
  /** Classes adicionais */
  className?: string;
  /** Mostrar apenas o ícone em mobile */
  hideTextOnMobile?: boolean;
}

const sizeClasses = {
  sm: {
    container: "h-8 w-8",
    icon: "h-4 w-4",
    text: "text-base",
  },
  md: {
    container: "h-10 w-10",
    icon: "h-6 w-6",
    text: "text-lg",
  },
  lg: {
    container: "h-12 w-12",
    icon: "h-7 w-7",
    text: "text-xl",
  },
};

export function Logo({
  variant = "full",
  size = "md",
  asLink = false,
  className,
  hideTextOnMobile = false,
}: LogoProps) {
  const sizes = sizeClasses[size];

  const IconElement = () => (
    <div
      className={cn(
        "flex items-center justify-center rounded-lg bg-primary flex-shrink-0",
        sizes.container
      )}
      aria-hidden="true"
    >
      <Leaf className={cn("text-primary-foreground", sizes.icon)} />
    </div>
  );

  const TextElement = () => (
    <span
      className={cn(
        "font-display font-bold text-foreground",
        sizes.text,
        hideTextOnMobile && "hidden sm:inline"
      )}
    >
      {BRAND.name}
    </span>
  );

  const content = (
    <div className={cn("flex items-center gap-3", className)}>
      {(variant === "full" || variant === "icon") && <IconElement />}
      {(variant === "full" || variant === "text") && <TextElement />}
    </div>
  );

  if (asLink) {
    return (
      <Link
        to="/"
        className="flex items-center gap-3 hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
        aria-label={`${BRAND.name} - Ir para página inicial`}
      >
        {content}
      </Link>
    );
  }

  return content;
}
